import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import Text from '@amzn/meridian/text';
import React from 'react';
import { PutClient } from '../../clients';
import { useHrpsApi } from '../../clients/useHrpsApi';
import { AttachmentType, DocumentAttachment } from '../../types';
import { sanitizeFilenameISO88591 } from '../../utils/textUtils';
import { useTranslation } from '../blocks';
import { DocumentUploader } from './DocumentUploader';

type ClaimDocumentUploaderProps = {
    resellerClaimNumber: string;
    resellerId: string;
    username: string;
    onDocumentUploaded: (document: DocumentAttachment) => void;
};

const RESELLER_CLAIM_NUMBER_KEY = 'ClaimNumber';
const CLAIM_SUPPORT_DOC_DESCRIPTION = 'Supporting claim document uploaded from RP.';

export const ClaimDocumentUploader = ({
    resellerClaimNumber,
    resellerId,
    username,
    onDocumentUploaded,
}: ClaimDocumentUploaderProps) => {
    const {
        returnOrders: { requestAttachmentUploadUrl },
        claims: { addAttachmentToClaim },
    } = useHrpsApi();
    const { t } = useTranslation('claimDocuments');

    const uploadClaimDocuments = async (file: File): Promise<DocumentAttachment> => {
        const uploadUrlResponse = await requestAttachmentUploadUrl({
            key1Value: resellerId,
            key2Name: RESELLER_CLAIM_NUMBER_KEY,
            key2Value: resellerClaimNumber,
            filename: sanitizeFilenameISO88591(file.name),
            type: AttachmentType.CLAIM_ATTACHMENT_UPLOAD,
            mimeType: file.type,
            description: CLAIM_SUPPORT_DOC_DESCRIPTION,
            visibleToReseller: true,
        });

        await PutClient.put(uploadUrlResponse.url, file);

        await addAttachmentToClaim({
            resellerClaimNumber,
            resellerId,
            attachment: {
                attachmentId: uploadUrlResponse.id,
                attachmentName: file.name,
                attachmentUser: username,
            },
        });

        return {
            attachmentId: uploadUrlResponse.id,
            attachmentName: file.name,
            attachmentUser: username,
        };
    };

    return (
        <Box type={'outline'} spacingInset={'none'} width={'100%'}>
            <Box spacingInset={'medium'} backgroundColor={'secondary'}>
                <Text type={'h200'}>{t('claim-document-uploader-title')}</Text>
            </Box>
            <Box spacingInset={'500'}>
                <Column alignmentHorizontal={'center'}>
                    <DocumentUploader
                        description={t('claim-document-uploader-description')}
                        acceptedFileFormats={'.pdf,.jpg,.jpeg,.png,.doc,.docx,.xlsx,.csv'}
                        uploadButtonLabel={t('claim-document-uploader-button-label')}
                        onUpload={uploadClaimDocuments}
                        onDocumentUploaded={onDocumentUploaded}
                    />
                </Column>
            </Box>
        </Box>
    );
};
